import React from 'react';
import { Typography, theme } from 'antd';
import { CaretDownFilled, CaretUpFilled, ExclamationCircleFilled } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';
import {
  type KPIAbsoluteComparison,
  KPI_ABSOLUTE_COMPARISON_TOOLTIP_MESSAGES,
} from './KPIAbsoluteComparison';
import { formatErrorRateDelta } from './ErrorRateFormatting';

export type KPIAbsoluteDeltaFormat = 'integer' | 'percentage' | 'errorRate';

export type KPIAbsoluteDeltaProps = {
  comparison: KPIAbsoluteComparison;
  format?: KPIAbsoluteDeltaFormat;
  inverted?: boolean;
};

function resolveColorType(isPositive: boolean, inverted: boolean): 'success' | 'danger' {
  const isGood = isPositive !== inverted;
  return isGood ? 'success' : 'danger';
}

function resolveDisplayText(value: number, format: KPIAbsoluteDeltaFormat): string {
  if (format === 'percentage') {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }
  if (format === 'errorRate') {
    return formatErrorRateDelta(value);
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}`;
}

function KPIAbsoluteDelta({
  comparison,
  format = 'integer',
  inverted = false,
}: KPIAbsoluteDeltaProps) {
  const { token } = theme.useToken();

  if (comparison.kind !== 'delta') {
    return (
      <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>
        <span style={{ marginRight: token.marginXXS }}>N/A</span>
        <CustomTooltip
          placement="top"
          message={KPI_ABSOLUTE_COMPARISON_TOOLTIP_MESSAGES[comparison.kind]}
        >
          <ExclamationCircleFilled />
        </CustomTooltip>
      </Typography.Text>
    );
  }

  const isPositive = comparison.value > 0;
  const colorType = resolveColorType(isPositive, inverted);
  const displayText = resolveDisplayText(comparison.value, format);

  // Figma uses Semi Bold (LG/Strong) for integer deltas and Regular (LG/Normal)
  // for percentage / number deltas. The 16px size + 24px line-height come from
  // the Figma LG token; we apply them via inline style so the surrounding
  // Typography.Text type="success|danger" still drives the color.
  const isStrong = format === 'integer';

  return (
    <Typography.Text
      strong={isStrong}
      type={colorType}
      style={{ fontSize: token.fontSizeLG, lineHeight: 1.5 }}
    >
      {displayText} {isPositive ? <CaretUpFilled /> : <CaretDownFilled />}
    </Typography.Text>
  );
}

export default KPIAbsoluteDelta;
